import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RootLayout } from './components/layout/RootLayout';
import { Shell } from './components/layout/Shell';
import { RequireAuth } from './components/layout/RequireAuth';
import { HomePage } from './routes/home/HomePage';
import LoginPage from './routes/auth/LoginPage';

/**
 * Top-level route config.
 *
 * Structure:
 *   /               → redirect to /app (RequireAuth bounces to /login if needed)
 *   /login          (PEL-821 — LoginPage, unauth)
 *   /signup         (PEL-821 — SignupPage, unauth)
 *   /verify-phone   (PEL-821 — PhoneVerifyPage, auth required)
 *   /forgot-password(PEL-821 — ForgotPasswordPage, unauth)
 *   /reset-password/:token (PEL-821 — ResetPasswordPage, unauth)
 *   /suspended      (PEL-821 — SuspendedPage, auth required)
 *   /app/*          → RequireAuth → Shell → nested routes
 *       /app                  (Home — PEL-823 replaces stub)
 *       /app/orders           (Order History — PEL-833 PR A)
 *       /app/orders/:orderId  (Order Detail — PEL-833 PR B)
 *       /app/orders/:orderId/confirm (Order Confirmation — PEL-832, post-checkout landing)
 *       /app/drafts           (PEL-823 — drafts list)
 *       /app/send/:recipientId/select (Product Select — PEL-824)
 *       /app/send/:recipientId/photos (Photo Upload — PEL-825)
 *       /app/products/photos  (PEL-823 placeholder → PEL-825)
 *       /app/products/letters (PEL-823 placeholder → PEL-826)
 *       /app/products/cards   (PEL-823 placeholder → PEL-828)
 *       /app/products/postcards (PEL-823 placeholder → PEL-827)
 *       /app/inbox            (PEL-836)
 *       /app/inbox/:conversationId (PEL-836 PR B — conversation detail)
 *       /app/wallet           (PEL-834)
 *       /app/pelipoints       (PEL-835)
 *       /app/profile          (PEL-837)
 *   /dev/ui         → shadcn primitives sandbox, DEV-only (PEL-816)
 *   *               → 404 page
 */

// Lazy-load non-critical leaf routes to keep the initial bundle small.
const OrdersPage = lazy(() => import('./routes/orders/OrdersPage'));
const OrderConfirmationPage = lazy(
  () => import('./routes/orders/OrderConfirmationPage'),
);
const OrderDetailPage = lazy(
  () => import('./routes/orders/OrderDetailPage'),
);
const InboxPage = lazy(() => import('./routes/inbox/InboxPage'));
const ConversationPage = lazy(
  () => import('./routes/inbox/ConversationPage'),
);
const WalletPage = lazy(() => import('./routes/wallet/WalletPage'));
const AddFundsPage = lazy(() => import('./routes/wallet/AddFundsPage'));
const PaymentMethodsPage = lazy(
  () => import('./routes/wallet/PaymentMethodsPage'),
);
const PeliPointsPage = lazy(
  () => import('./routes/pelipoints/PeliPointsPage'),
);
const RedeemPage = lazy(() => import('./routes/pelipoints/RedeemPage'));
const ProfilePage = lazy(() => import('./routes/profile/ProfilePage'));
const NotFoundPage = lazy(() => import('./routes/NotFoundPage'));

// Recipients (PEL-822) — the list is likely hit on first session after signup
// so keeping it eager avoids a skeleton flash; the others are leaf routes.
const RecipientsListPage = lazy(
  () => import('./routes/recipients/RecipientsListPage'),
);
const NewRecipientPage = lazy(
  () => import('./routes/recipients/NewRecipientPage'),
);
const RecipientDetailPage = lazy(
  () => import('./routes/recipients/RecipientDetailPage'),
);
const RecipientRulesPage = lazy(
  () => import('./routes/recipients/RecipientRulesPage'),
);
const RecipientAttributesPage = lazy(
  () => import('./routes/recipients/RecipientAttributesPage'),
);
const AddRecipientOnboardingPage = lazy(
  () => import('./routes/onboarding/AddRecipientOnboardingPage'),
);

// Drafts (PEL-823)
const DraftsListPage = lazy(() => import('./routes/drafts/DraftsListPage'));

// Product flows (PEL-823) — placeholders until PEL-825/826/827/828 ship.
// Wired now so the Quick Action tiles on the home screen have real routes
// instead of 404s.
const PhotosProductPage = lazy(
  () => import('./routes/products/PhotosProductPage'),
);
const PhotoPreviewPage = lazy(
  () => import('./routes/products/photos/PhotoPreviewPage'),
);
const LetterProductPage = lazy(
  () => import('./routes/products/LetterProductPage'),
);
// Cards (PEL-828) — REDUCED v1: template picker + text overlay only.
const CardTemplatePickerPage = lazy(
  () => import('./routes/products/cards/CardTemplatePickerPage'),
);
const CardEditorPage = lazy(
  () => import('./routes/products/cards/CardEditorPage'),
);
// PEL-826 — per-recipient letter write screen.
const LetterWritePage = lazy(
  () => import('./routes/products/letters/LetterWritePage'),
);
const PostcardProductPage = lazy(
  () => import('./routes/products/PostcardProductPage'),
);

// Postcard upload + preview flow (PEL-827). Recipient-scoped routes mirror
// the letter flow shipped in PEL-826 and the mobile photo-upload postcard
// flow — user picks one or more photos, then writes the message on the back.
const PostcardUploadPage = lazy(
  () => import('./routes/products/postcards/PostcardUploadPage'),
);
const PostcardPreviewPage = lazy(
  () => import('./routes/products/postcards/PostcardPreviewPage'),
);

// Product Select (PEL-824) — the bridge between recipient + product flow.
const ProductSelectPage = lazy(
  () => import('./routes/products/ProductSelectPage'),
);

// Photo Upload (PEL-825) — the full-fat photo flow (HEIC, EXIF, moderation,
// WYSIWYG crop editor, size/qty).
const PhotoUploadPage = lazy(
  () => import('./routes/products/photos/PhotoUploadPage'),
);

// Cart (PEL-830) — consumer cart view with totals, promo, and checkout CTA.
const CartPage = lazy(() => import('./routes/cart/CartPage'));

// Checkout (PEL-831 PR A) — shell + order summary + facility destination.
// Payment fields land in PR B (Stax) and PR C (wallet/points/split-pay).
const CheckoutPage = lazy(() => import('./routes/checkout/CheckoutPage'));

// Auth screens other than /login are lazy — most visitors hit /login first
// and never see the others, so we don't need them in the initial bundle.
const SignupPage = lazy(() => import('./routes/auth/SignupPage'));
const PhoneVerifyPage = lazy(() => import('./routes/auth/PhoneVerifyPage'));
const ForgotPasswordPage = lazy(
  () => import('./routes/auth/ForgotPasswordPage'),
);
const ResetPasswordPage = lazy(
  () => import('./routes/auth/ResetPasswordPage'),
);
const SuspendedPage = lazy(() => import('./routes/auth/SuspendedPage'));

// Dev-only UI primitives sandbox (PEL-816). Lazy + gated by import.meta.env.DEV
// so the sandbox + its whole component graph are fully dead-code eliminated
// from production bundles.
const UiSandboxPage = import.meta.env.DEV
  ? lazy(() => import('./routes/dev/UiSandboxPage'))
  : null;

const lazyFallback = <div className="px-6 py-10">Loading…</div>;

function L({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={lazyFallback}>{children}</Suspense>;
}

// Child routes of the root layout. AuthProvider lives in RootLayout, so every
// route below can safely call useAuth / useNavigate.
const rootChildren = [
  {
    index: true,
    element: <Navigate to="/app" replace />,
  },
  // Unauthenticated auth screens — see PEL-821.
  { path: 'login', element: <LoginPage /> },
  { path: 'signup', element: <L><SignupPage /></L> },
  { path: 'forgot-password', element: <L><ForgotPasswordPage /></L> },
  { path: 'reset-password/:token', element: <L><ResetPasswordPage /></L> },
  // Phone verify + suspended require an authenticated user but don't live
  // inside the /app shell — they are full-page chrome.
  { path: 'verify-phone', element: <L><PhoneVerifyPage /></L> },
  { path: 'suspended', element: <L><SuspendedPage /></L> },
  // Onboarding (PEL-822) — requires an authenticated user but renders its
  // own full-page chrome (AuthCard), so it lives outside the /app shell.
  {
    path: 'onboarding',
    element: <RequireAuth />,
    children: [
      {
        path: 'add-recipient',
        element: <L><AddRecipientOnboardingPage /></L>,
      },
    ],
  },
  {
    path: 'app',
    element: <RequireAuth />,
    children: [
      {
        element: <Shell />,
        children: [
          { index: true, element: <HomePage /> },
          // Recipients (PEL-822)
          { path: 'recipients', element: <L><RecipientsListPage /></L> },
          { path: 'recipients/new', element: <L><NewRecipientPage /></L> },
          { path: 'recipients/:id', element: <L><RecipientDetailPage /></L> },
          { path: 'recipients/:id/rules', element: <L><RecipientRulesPage /></L> },
          { path: 'recipients/:id/attributes', element: <L><RecipientAttributesPage /></L> },
          { path: 'orders', element: <L><OrdersPage /></L> },
          // PEL-833 PR B — order detail + reorder (timeline, tracking, items,
          // payment breakdown, reorder CTA). This is the canonical direct-link
          // target from order history and the confirmation email CTA.
          { path: 'orders/:orderId', element: <L><OrderDetailPage /></L> },
          // PEL-832 — celebratory post-checkout confirmation / receipt page.
          // Moved to /confirm so checkout success still lands here while
          // /orders/:orderId resolves to the detail view above.
          { path: 'orders/:orderId/confirm', element: <L><OrderConfirmationPage /></L> },
          { path: 'drafts', element: <L><DraftsListPage /></L> },
          { path: 'send/:recipientId/select', element: <L><ProductSelectPage /></L> },
          { path: 'send/:recipientId/photos', element: <L><PhotoUploadPage /></L> },
          { path: 'products/photos', element: <L><PhotosProductPage /></L> },
          { path: 'send/:recipientId/photos/preview', element: <L><PhotoPreviewPage /></L> },
          { path: 'products/letters', element: <L><LetterProductPage /></L> },
          { path: 'send/:recipientId/letter', element: <L><LetterWritePage /></L> },
          // Cards (PEL-828) — picker list + editor.
          { path: 'products/cards', element: <L><CardTemplatePickerPage /></L> },
          { path: 'products/cards/:templateId/edit', element: <L><CardEditorPage /></L> },
          { path: 'products/postcards', element: <L><PostcardProductPage /></L> },
          // PEL-827 — recipient-scoped postcard flow (matches mobile).
          { path: 'send/:recipientId/postcards', element: <L><PostcardUploadPage /></L> },
          { path: 'send/:recipientId/postcards/preview', element: <L><PostcardPreviewPage /></L> },
          // PEL-830 — consumer cart.
          { path: 'cart', element: <L><CartPage /></L> },
          // PEL-831 — consumer checkout. PR A ships the shell; payment
          // wiring follows in subsequent PRs.
          { path: 'checkout', element: <L><CheckoutPage /></L> },
          { path: 'inbox', element: <L><InboxPage /></L> },
          {
            path: 'inbox/:conversationId',
            element: <L><ConversationPage /></L>,
          },
          { path: 'wallet', element: <L><WalletPage /></L> },
          // PEL-834 PR B — add funds (preset amounts + Stax-tokenized new card).
          { path: 'wallet/add-funds', element: <L><AddFundsPage /></L> },
          // PEL-834 PR C — saved payment methods management.
          {
            path: 'wallet/payment-methods',
            element: <L><PaymentMethodsPage /></L>,
          },
          { path: 'pelipoints', element: <L><PeliPointsPage /></L> },
          {
            path: 'pelipoints/redeem',
            element: <L><RedeemPage /></L>,
          },
          { path: 'profile', element: <L><ProfilePage /></L> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <L><NotFoundPage /></L>,
  },
];

if (import.meta.env.DEV && UiSandboxPage) {
  // Insert before the '*' catch-all.
  rootChildren.splice(rootChildren.length - 1, 0, {
    path: 'dev/ui',
    element: <L><UiSandboxPage /></L>,
  });
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: rootChildren,
  },
]);
